import { useMemo } from 'react';
import { css } from '@linaria/core';
import clsx from 'clsx';
import { ResponsiveProps } from '@cbhq/cds-web';
import { Icon } from '@cbhq/cds-web/icons';
import { Box, Grid, HStack, VStack } from '@cbhq/cds-web/layout';
import { Avatar } from '@cbhq/cds-web/media';
import {
  TextHeadline,
  TextLegal,
  TextTitle1,
  TextTitle3,
  TextTitle4,
} from '@cbhq/cds-web/typography';

import { useSimpleBreakpoints } from ':cloud/hooks/useSimpleBreakpoints';
import { useGetUser } from ':cloud/queries/UserQueries/useGetUser';
import CDPQuestLogo from ':cloud/widgets/buildQuest/assets/cdpQuestLogo.png';
import { useBuildQuestContext } from ':cloud/widgets/buildQuest/BuildQuestProvider';
import { confettiRain } from ':cloud/widgets/buildQuest/confetti';
import { ProgressBarSection } from ':cloud/widgets/buildQuest/PlayerPanelProgressBarSection';

function getAbbreviatedName(fullName: string) {
  const nameParts = fullName.split(' ');

  if (nameParts.length === 1) {
    return nameParts[0];
  }

  return `${nameParts[0]} ${nameParts[nameParts.length - 1][0]}.`;
}

type PlayerInfoProps = {
  playerName: string;
};
function PlayerName({ playerName }: PlayerInfoProps) {
  const { isPhone, isTablet } = useSimpleBreakpoints();
  if (isPhone) {
    return (
      <TextHeadline as="h2" overflow="truncate">
        {getAbbreviatedName(playerName)}
      </TextHeadline>
    );
  }
  if (isTablet) {
    return (
      <TextTitle3 as="h2" overflow="truncate">
        {getAbbreviatedName(playerName)}
      </TextTitle3>
    );
  }
  return (
    <TextTitle1 as="h2" overflow="truncate">
      {getAbbreviatedName(playerName)}
    </TextTitle1>
  );
}

function PlayerAvatar({ playerName }: PlayerInfoProps) {
  const { isPhone, isTablet } = useSimpleBreakpoints();
  const avatarSize = useMemo(
    () => (isPhone ? 'xl' : isTablet ? 'xxl' : 'xxxl'),
    [isPhone, isTablet],
  );
  return <Avatar size={avatarSize} alt={`${playerName}'s avatar`} name={playerName} />;
}

const responsiveBoxConfig: ResponsiveProps = {
  phone: { flexDirection: 'column', gap: 2 },
  tablet: { flexDirection: 'column', gap: 4 },
  desktop: { flexDirection: 'row' },
};

export function PlayerPanel() {
  const { isPhone, isTablet, isDesktop } = useSimpleBreakpoints();
  const { user } = useGetUser();

  const { playerLevel, currentLevelColor, displayedPoints, togglePrizesModalOn } =
    useBuildQuestContext();

  const playerName = user.displayName;

  const backgroundBlur = css`
    backdrop-filter: blur(20px);
  `;

  const boxSpacingVertical = useMemo(() => (isDesktop ? 5 : 2), [isDesktop]);
  const boxSpacingHorizontal = useMemo(() => (isDesktop ? 5 : 4), [isDesktop]);
  const gridTemplateColumns = useMemo(() => (isDesktop ? '1fr 3fr 1fr' : '1fr 1fr'), [isDesktop]);
  const imageHeight = useMemo(() => (isDesktop ? undefined : 40), [isDesktop]);
  return (
    <Box
      className={clsx(backgroundBlur, confettiRain)}
      position="sticky"
      bottom={0}
      width="100%"
      alignItems="center"
      justifyContent="space-between"
      dangerouslySetBackground="rgba(10, 11, 13, 0.5)"
      spacingVertical={boxSpacingVertical}
      spacingHorizontal={boxSpacingHorizontal}
      responsiveConfig={responsiveBoxConfig}
      id="confetti-wrapper"
      overflow="hidden"
    >
      {(isPhone || isTablet) && (
        <ProgressBarSection
          playerLevel={playerLevel}
          currentLevelColor={currentLevelColor}
          displayedPoints={displayedPoints}
          width="100%"
          onPressPrizes={togglePrizesModalOn}
        />
      )}
      <Grid width="100%" templateColumns={gridTemplateColumns} gap={3} justifyContent="center">
        <img src={CDPQuestLogo} alt="CDP Quest" height={imageHeight} width="auto" />
        {isDesktop && (
          <ProgressBarSection
            playerLevel={playerLevel}
            currentLevelColor={currentLevelColor}
            displayedPoints={displayedPoints}
            onPressPrizes={togglePrizesModalOn}
          />
        )}

        {/* Player Name and Level */}
        <HStack gap={3} justifyContent="flex-end" width="100%" alignItems="center">
          <VStack>
            <PlayerName playerName={playerName} />
            <HStack gap={1} alignItems="center">
              {isDesktop ? (
                <TextTitle4 as="p" dangerouslySetColor={currentLevelColor}>
                  Level {playerLevel}
                </TextTitle4>
              ) : (
                <TextLegal as="p" dangerouslySetColor={currentLevelColor}>
                  Level {playerLevel}
                </TextLegal>
              )}
              <HStack>
                {Array.from({ length: playerLevel }).map((_, index) => (
                  <Icon
                    // eslint-disable-next-line react/no-array-index-key
                    key={index}
                    name="nftSaleActive"
                    size="s"
                    dangerouslySetColor={currentLevelColor}
                  />
                ))}
              </HStack>
            </HStack>
          </VStack>
          <PlayerAvatar playerName={playerName} />
        </HStack>
      </Grid>
    </Box>
  );
}
